/**
 * recurringOptions.ts
 *
 * Shared constants for recurring / payment-plan configuration.
 * Import these wherever recurring donation options are rendered so that
 * labels, values, and defaults stay consistent across all pages.
 *
 * TransactionModel.recurringFrequency mapping:
 *   1 = Monthly  |  2 = Yearly  |  3 = Daily
 */

// ---------------------------------------------------------------------------
// Threshold
// ---------------------------------------------------------------------------

/** Donation amounts at or above this value automatically suggest a payment plan */
export const LARGE_AMOUNT_THRESHOLD = 1000;

// ---------------------------------------------------------------------------
// Frequency options
// ---------------------------------------------------------------------------

export interface RecurringFrequency {
  /** Human-readable label shown on the button */
  label: string;
  /** Numeric value sent to the API (1=Monthly, 2=Yearly, 3=Daily) */
  value: number;
  /** Count pre-selected when this frequency is chosen */
  defaultCount: number;
  /** Singular time unit used in "per X" breakdowns */
  unit: string;
  /** Question label shown above the count picker */
  countQuestion: string;
}

export const RECURRING_FREQUENCIES: RecurringFrequency[] = [
  { label: 'Daily', value: 3, defaultCount: 30, unit: 'day', countQuestion: 'For how many days?' },
  {
    label: 'Monthly',
    value: 1,
    defaultCount: 12,
    unit: 'month',
    countQuestion: 'For how many months?',
  },
  {
    label: 'Yearly',
    value: 2,
    defaultCount: 1,
    unit: 'year',
    countQuestion: 'For how many years?',
  },
];

// ---------------------------------------------------------------------------
// Count options per frequency
// ---------------------------------------------------------------------------

export interface RecurringCountOption {
  value: number;
  label: string;
}

/** All available count choices keyed by frequency value */
export const RECURRING_COUNTS: Record<number, RecurringCountOption[]> = {
  // Daily
  3: [
    { value: 5, label: '5 Days' },
    { value: 7, label: '7 Days' },
    { value: 9, label: '9 Days' },
    { value: 10, label: '10 Days' },
    { value: 15, label: '15 Days' },
    { value: 20, label: '20 Days' },
    { value: 25, label: '25 Days' },
    { value: 30, label: '30 Days' },
  ],
  // Monthly
  1: [
    { value: 1, label: '1 Month' },
    { value: 2, label: '2 Months' },
    { value: 3, label: '3 Months' },
    { value: 4, label: '4 Months' },
    { value: 5, label: '5 Months' },
    { value: 6, label: '6 Months' },
    { value: 7, label: '7 Months' },
    { value: 8, label: '8 Months' },
    { value: 9, label: '9 Months' },
    { value: 10, label: '10 Months' },
    { value: 12, label: '12 Months' },
    { value: 15, label: '15 Months' },
    { value: 18, label: '18 Months' },
    { value: 24, label: '24 Months' },
    { value: 36, label: '36 Months' },
  ],
  // Yearly
  2: [
    { value: 1, label: '1 Year' },
    { value: 2, label: '2 Years' },
    { value: 3, label: '3 Years' },
    { value: 4, label: '4 Years' },
    { value: 5, label: '5 Years' },
  ],
};

// ---------------------------------------------------------------------------
// Defaults (used to initialise state and reset on toggle-off)
// ---------------------------------------------------------------------------

/** Default frequency value when a payment plan is first suggested */
export const DEFAULT_RECURRING_FREQUENCY = 1; // Monthly

/** Default count when a payment plan is first suggested */
export const DEFAULT_RECURRING_COUNT = 12;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Returns the FrequencyOption for a given value, falling back to Monthly */
export function getFrequencyInfo(value: number): RecurringFrequency {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  return RECURRING_FREQUENCIES.find((f) => f.value === value) ?? RECURRING_FREQUENCIES[1]!;
}
